import React from "react";
import { useParams, Navigate, Link as RouterLink } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  CardContent,
  Button,
  Grid,
  Breadcrumbs,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import { motion } from "framer-motion";
import { REACT_APP_SITE_URL } from "../config/seo";
import {
  generateArticleStructuredData,
  generateBreadcrumbStructuredData,
} from "../utils/structuredData";
import { blogPosts } from "../data/blogPosts";
import { SEO } from "../components/SEO";

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = blogPosts
    .filter((p) => p.slug !== post.slug)
    .filter(
      (p) =>
        p.category === post.category ||
        p.tags.some((t) => post.tags.includes(t)),
    )
    .slice(0, 3);

  const postUrl = `${REACT_APP_SITE_URL}/blog/${post.slug}`;
  const postImage = post.coverImage
    ? `${REACT_APP_SITE_URL}${post.coverImage}`
    : `${REACT_APP_SITE_URL}/logo.png`;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <SEO
        title={post.title}
        description={post.excerpt}
        image={postImage}
        article
        publishedTime={new Date(post.publishedAt).toISOString()}
        keywords={post.tags}
        jsonLd={[
          generateArticleStructuredData({
            title: post.title,
            description: post.excerpt,
            url: postUrl,
            publishedTime: new Date(post.publishedAt).toISOString(),
            authorName: post.author,
            authorUrl: REACT_APP_SITE_URL,
            image: postImage,
          }),
          generateBreadcrumbStructuredData([
            { name: "Home", item: `${REACT_APP_SITE_URL}/` },
            { name: "Blog", item: `${REACT_APP_SITE_URL}/blog` },
            { name: post.title, item: postUrl },
          ]),
        ]}
      />

      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
        {/* Breadcrumb */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 4 }}>
          <MuiLink
            component={RouterLink}
            to="/"
            color="inherit"
            underline="hover"
          >
            Home
          </MuiLink>
          <MuiLink
            component={RouterLink}
            to="/blog"
            color="inherit"
            underline="hover"
          >
            Blog
          </MuiLink>
          <Typography color="text.primary" noWrap sx={{ maxWidth: 300 }}>
            {post.title}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={5}>
          {/* Main content */}
          <Grid size={{ xs: 12, md: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Post header */}
              <Chip
                label={post.category}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ mb: 2 }}
              />

              <Typography
                variant="h3"
                component="h1"
                sx={{ fontWeight: 700, lineHeight: 1.25, mb: 2 }}
              >
                {post.title}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  mb: 4,
                  color: "text.secondary",
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2">{post.author}</Typography>
                <Typography variant="body2">·</Typography>
                <Typography variant="body2">
                  {new Date(post.publishedAt).toLocaleDateString("en-IN", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
                <Typography variant="body2">·</Typography>
                <Typography variant="body2">
                  {post.readTime} min read
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Cover image */}
              {post.coverImage && (
                <Box
                  sx={{
                    mb: 4,
                    bgcolor: "grey.100",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                    maxHeight: 320,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={post.coverImage}
                    alt={post.title}
                    sx={{
                      maxHeight: 280,
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              {/* Article body */}
              <Box
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  "& h2": {
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "text.primary",
                    mt: 4,
                    mb: 2,
                  },
                  "& h3": {
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    color: "text.primary",
                    mt: 3,
                    mb: 1.5,
                  },
                  "& p": {
                    margin: "0 0 20px 0",
                    "&:last-child": { marginBottom: 0 },
                  },
                  "& ul, & ol": {
                    margin: "16px 0",
                    paddingLeft: "28px",
                    "& li": {
                      marginBottom: "10px",
                      "&:last-child": { marginBottom: 0 },
                    },
                  },
                  "& strong": {
                    color: "text.primary",
                    fontWeight: 600,
                  },
                  "& a": {
                    color: "primary.main",
                    textDecoration: "underline",
                    "&:hover": { textDecoration: "none" },
                  },
                  "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    margin: "24px 0",
                    fontSize: "0.9rem",
                    overflowX: "auto",
                    display: "block",
                  },
                  "& th": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    padding: "10px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  },
                  "& td": {
                    padding: "10px 12px",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    verticalAlign: "top",
                  },
                  "& tr:nth-of-type(even) td": {
                    bgcolor: "action.hover",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(post.content),
                }}
              />

              {/* Tags */}
              <Box sx={{ mt: 5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {post.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem" }}
                  />
                ))}
              </Box>

              <Box sx={{ mt: 4 }}>
                <Button
                  component={RouterLink}
                  to="/blog"
                  variant="outlined"
                  size="small"
                >
                  ← Back to Blog
                </Button>
              </Box>
            </motion.div>
          </Grid>

          {/* Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {related.length > 0 && (
                <Box>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: 700, mb: 3 }}
                  >
                    Related Articles
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {related.map((rel) => (
                      <Card
                        key={rel.slug}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          transition: "box-shadow 0.2s",
                          "&:hover": { boxShadow: 3 },
                        }}
                      >
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Chip
                            label={rel.category}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ mb: 1 }}
                          />
                          <Typography
                            variant="subtitle2"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              lineHeight: 1.3,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {rel.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 1.5 }}
                          >
                            {rel.readTime} min read
                          </Typography>
                          <Button
                            component={RouterLink}
                            to={`/blog/${rel.slug}`}
                            size="small"
                            variant="text"
                            sx={{ p: 0, minWidth: 0 }}
                          >
                            Read →
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}

              {/* CTA card */}
              <Card
                sx={{
                  mt: 4,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Need Help Choosing?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Our team at R.S. Enterprises has been helping retail
                    businesses pick the right billing solution since 2011.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: "common.white",
                      color: "primary.main",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    Contact Us
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BlogPost;
